import { useCallback } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { GOAL_QUERY_KEYS } from './keys';

import { urls } from 'constants/jump';

import * as feedbackService from 'services/feedback';
import * as goalService from 'services/goal';

import { interpolate } from 'utils/string';

type GoalParams = Record<string, string | number | boolean | undefined>;

export interface UserGoal {
  id: number | string;
  [key: string]: unknown;
}

export interface UserGoalSummary {
  type: string;
  count: number | string;
  [key: string]: unknown;
}

export interface GoalFeedback {
  goal: {
    id: number | string;
  };
  [key: string]: unknown;
}

export interface UserFeedback {
  feedbackId: number | string;
  goal?: object;
  [key: string]: unknown;
}

export interface UserFeedbackWithLink extends UserFeedback {
  link: string;
}

export const getUserGoalsQueryKey = (employeeId?: number | string, params?: GoalParams) =>
  [GOAL_QUERY_KEYS.USER_GOALS, employeeId, params] as const;

export const getUserGoalSummaryQueryKey = (employeeId?: number | string, params?: GoalParams) =>
  [GOAL_QUERY_KEYS.USER_GOALS_SUMMARY, employeeId, params] as const;

export const getUserFeedbacksQueryKey = (employeeId?: number | string, params?: GoalParams) =>
  [GOAL_QUERY_KEYS.USER_FEEDBACKS, employeeId, params] as const;

export const getGoalFeedbackQueryKey = (goalId?: number | string, params?: GoalParams) =>
  [GOAL_QUERY_KEYS.GOAL_FEEDBACKS, goalId, params] as const;

export const useUserGoalsQuery = (
  employeeId?: number | string,
  params?: GoalParams,
  enabled = true
): UseQueryResult<UserGoal[], Error> => {
  return useQuery<UserGoal[], Error>({
    queryKey: getUserGoalsQueryKey(employeeId, params),
    queryFn: () => goalService.fetchUserGoals(employeeId ?? '', params || {}) as Promise<UserGoal[]>,
    enabled: Boolean(employeeId) && enabled,
  });
};

export const useUserGoalSummaryQuery = (
  employeeId?: number | string,
  params?: GoalParams,
  enabled = true
): UseQueryResult<UserGoalSummary[], Error> => {
  return useQuery<UserGoalSummary[], Error>({
    queryKey: getUserGoalSummaryQueryKey(employeeId, params),
    queryFn: () => goalService.fetchUserGoalsSummary(employeeId ?? '', params || {}) as Promise<UserGoalSummary[]>,
    enabled: Boolean(employeeId) && enabled,
  });
};

export const useUserFeedbacksQuery = (
  employeeId?: number | string,
  params?: GoalParams,
  enabled = true
): UseQueryResult<UserFeedbackWithLink[], Error> => {
  return useQuery<UserFeedback[], Error, UserFeedbackWithLink[]>({
    queryKey: getUserFeedbacksQueryKey(employeeId, params),
    queryFn: () => feedbackService.fetchUserFeedbacks(employeeId ?? '', params || {}) as Promise<UserFeedback[]>,
    select: (feedbacks) =>
      feedbacks.map((feedback) => ({
        ...feedback,
        link: interpolate(urls.FEEDBACK_DETAIL_VIEW, { id: feedback.feedbackId }),
      })),
    enabled: Boolean(employeeId) && enabled,
  });
};

export const useGoalFeedbacks = () => {
  const queryClient = useQueryClient();

  const fetchGoalFeedbacks = useCallback(
    (goalId: number | string, params?: GoalParams) =>
      queryClient.fetchQuery<GoalFeedback[], Error>({
        queryKey: getGoalFeedbackQueryKey(goalId, params),
        queryFn: () => feedbackService.fetchGoalFeedbacks(goalId, params || {}) as Promise<GoalFeedback[]>,
      }),
    [queryClient]
  );

  return {
    fetchGoalFeedbacks,
  };
};
